// BMI Calculator Utility
// Supports both pediatric BMI (with percentiles) and standard BMI

/**
 * Calculate age in months from date of birth
 * @param {string} dob - Date of birth in DD/MM/YYYY format
 * @returns {number} Age in months
 */
export const calculateAgeInMonths = (dob) => {
  if (!dob) return null;
  
  const [day, month, year] = dob.split('/').map(Number);
  const birthDate = new Date(year, month - 1, day);
  const today = new Date();
  
  let months = (today.getFullYear() - birthDate.getFullYear()) * 12;
  months += today.getMonth() - birthDate.getMonth();
  
  if (today.getDate() < birthDate.getDate()) {
    months--;
  }
  
  return months;
};

/**
 * Calculate standard BMI
 * @param {number} weightKg - Weight in kilograms
 * @param {number} heightCm - Height in centimeters
 * @returns {number} BMI value
 */
export const calculateStandardBMI = (weightKg, heightCm) => {
  if (!weightKg || !heightCm || heightCm === 0) return null;
  
  const heightM = heightCm / 100;
  const bmi = weightKg / (heightM * heightM);
  return Math.round(bmi * 10) / 10; // Round to 1 decimal
};

/**
 * Get BMI category for adults/standard BMI
 * @param {number} bmi - BMI value
 * @returns {object} Category info with status and color
 */
export const getStandardBMICategory = (bmi) => {
  if (!bmi) return { status: 'N/A', color: 'gray' };
  
  if (bmi < 18.5) return { status: 'Underweight', color: 'blue' };
  if (bmi < 25) return { status: 'Normal', color: 'green' };
  if (bmi < 30) return { status: 'Overweight', color: 'yellow' };
  return { status: 'Obese', color: 'red' };
};

/**
 * Calculate pediatric BMI percentile (simplified version)
 * Note: This is a simplified calculation. For accurate percentiles, 
 * use CDC/WHO growth charts with full percentile tables.
 * @param {number} bmi - BMI value
 * @param {number} ageMonths - Age in months
 * @param {string} gender - 'Male' or 'Female'
 * @returns {object} Percentile info with status and color
 */
export const getPediatricBMICategory = (bmi, ageMonths, gender) => {
  if (!bmi || !ageMonths) return { status: 'N/A', color: 'gray', percentile: null };
  
  // Simplified pediatric BMI categories based on age
  // For children 2-20 years (24-240 months)
  if (ageMonths < 24) {
    // For infants/toddlers under 2 years
    return { status: 'See Growth Chart', color: 'blue', percentile: null };
  }
  
  // Simplified percentile estimation (this should ideally use CDC/WHO charts)
  // These are approximate thresholds
  let percentile = null;
  let status = '';
  let color = '';
  
  // Age-adjusted approximate percentiles
  const ageYears = ageMonths / 12;
  
  // Rough approximation for demonstration
  // In production, use proper CDC/WHO percentile tables
  if (bmi < 14 + (ageYears * 0.3)) {
    percentile = '<5th';
    status = 'Underweight';
    color = 'blue';
  } else if (bmi < 17 + (ageYears * 0.4)) {
    percentile = '5th-85th';
    status = 'Normal';
    color = 'green';
  } else if (bmi < 20 + (ageYears * 0.5)) {
    percentile = '85th-95th';
    status = 'Overweight';
    color = 'yellow';
  } else {
    percentile = '>95th';
    status = 'Obese';
    color = 'red';
  }
  
  return { status, color, percentile };
};

/**
 * Main BMI calculation function
 * Determines whether to use pediatric or standard BMI based on age and doctor specialization
 * @param {number} weightKg - Weight in kilograms
 * @param {number} heightCm - Height in centimeters
 * @param {string} dob - Date of birth in DD/MM/YYYY format
 * @param {string} gender - 'Male' or 'Female'
 * @param {string} doctorSpecialization - Doctor's specialization (e.g., 'Pediatrician')
 * @returns {object} BMI info with value, status, color, and percentile (if pediatric)
 */
export const calculateBMI = (weightKg, heightCm, dob, gender, doctorSpecialization) => {
  const bmi = calculateStandardBMI(weightKg, heightCm);
  
  if (!bmi) {
    return { bmi: null, status: 'N/A', color: 'gray', percentile: null, isPediatric: false };
  }
  
  const ageMonths = calculateAgeInMonths(dob);
  
  // Use pediatric BMI if:
  // 1. Doctor is a pediatrician OR
  // 2. Patient is under 20 years (240 months)
  const isPediatric = doctorSpecialization?.toLowerCase().includes('pediatr') || 
                      (ageMonths && ageMonths < 240);
  
  if (isPediatric && ageMonths) {
    const pediatricCategory = getPediatricBMICategory(bmi, ageMonths, gender);
    return {
      bmi,
      status: pediatricCategory.status,
      color: pediatricCategory.color,
      percentile: pediatricCategory.percentile,
      isPediatric: true
    };
  } else {
    const standardCategory = getStandardBMICategory(bmi);
    return {
      bmi,
      status: standardCategory.status,
      color: standardCategory.color,
      percentile: null,
      isPediatric: false
    };
  }
};

/**
 * Get badge color class for Tailwind CSS
 * @param {string} color - Color name (green, yellow, red, blue, gray)
 * @returns {string} Tailwind CSS classes
 */
export const getBMIBadgeClasses = (color) => {
  const colorMap = {
    green: 'bg-green-100 text-green-800 border-green-200',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    red: 'bg-red-100 text-red-800 border-red-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200',
    gray: 'bg-gray-100 text-gray-800 border-gray-200'
  };
  
  return colorMap[color] || colorMap.gray;
};
